import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { Subject, takeUntil, timer } from 'rxjs';

import { AuthService } from '../auth.service';
import { LoginResponseModel } from '../models/login-response.model';

@Component({
  selector: 'app-candidate-register',
  templateUrl: './candidate-register.component.html',
  styleUrls: ['./candidate-register.component.scss'],
})
export class CandidateRegisterComponent implements OnInit, OnDestroy {
  public registerFormGroup: FormGroup;
  public isLoading = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  public hidePassword = true;
  public hideConfirmPassword = true;

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private socialAuthService: SocialAuthService
  ) {
    this.registerFormGroup = this.formBuilder.group({
      firstname: [null, [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
      lastname: [null, [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
      email: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required, Validators.minLength(8)]],
      confirmPassword: [null, Validators.required],
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/candidate']);
    }

    this.socialAuthService.authState
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: SocialUser) => {
        if (user) {
          this.handleSocialLogin(user);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');

    if (!password || !confirmPassword) {
      return null;
    }

    if (password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    // Clear passwordMismatch error if passwords match, but keep other errors
    if (confirmPassword.hasError('passwordMismatch')) {
      const errors = { ...confirmPassword.errors };
      delete errors['passwordMismatch'];
      confirmPassword.setErrors(Object.keys(errors).length ? errors : null);
    }

    return null;
  }

  public register(): void {
    if (this.registerFormGroup.valid) {
      this.isLoading = true;
      this.errorMessage = null;
      this.successMessage = null;

      const { firstname, lastname, email, password } = this.registerFormGroup.value;

      this.authService.registerCandidate({ firstname, lastname, email, password })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: LoginResponseModel) => {
            this.isLoading = false;
            localStorage.setItem('token', response.token);
            localStorage.setItem('role', response.role);
            localStorage.setItem('userId', response.userId + '');
            localStorage.setItem('fullName', response.fullName);
            sessionStorage.setItem('authenticated', 'true');

            this.successMessage = 'Registration successful! Please check your email to verify your account.';
            timer(2000)
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => this.router.navigate(['/candidate']));
          },
          error: (error) => {
            this.isLoading = false;
            if (error.status === 409) {
              this.errorMessage = 'An account with this email already exists.';
            } else {
              this.errorMessage = error.error?.message || 'Registration failed. Please try again.';
            }
          }
        });
    }
  }

  private async handleSocialLogin(user: SocialUser): Promise<void> {
    try {
      this.isLoading = true;
      this.errorMessage = null;
      await this.authService.loginSocial(user);
      this.router.navigate(['/candidate']);
    } catch (error) {
      this.errorMessage = 'Social login failed. Please try again.';
      this.isLoading = false;
    }
  }

  public navigateToLogin(): void {
    this.router.navigate(['/candidate-login']);
  }
}
